/**
 * code.state.ts — the buyer's voucher field, as the two cells the wire splits
 * into: what this browser owns, and what the server has said back.
 *
 * `voucherDraft` is the client's own — what stands in the field this second,
 * seen by nobody until the buyer submits.
 *
 * `voucherEntry` is everything the browser is ever allowed to know about a
 * code. The storefront can never read a voucher document at any tier, so this
 * cell mirrors `checkout.voucherResult` + `checkout.voucherAttempts` and
 * nothing else: whether the code applied, how much came off, which types it
 * revealed, and how many refusals this checkout has spent. There is no cause
 * field and no place a later change could put one.
 *
 * `phase` is one cell rather than a set of booleans because the states are
 * exclusive: a surface that can be two of them at once has a state nobody
 * designed. Two of the five are the client naming its own act rather than the
 * server's answer — `pending` is a submission with no answer yet, and
 * `dropped` separates a code the buyer's own basket edit re-resolved away from
 * one that never applied at all, which is the whole reason the neutral-refusal
 * rule does not bind it: `dropped` is about a code this buyer already saw
 * accepted, amount and all, so saying it no longer fits their selection
 * discloses nothing they were not shown a moment ago.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { type Checkout, Voucher } from "@momo/types";
import { Subject } from "rxjs";

/** The shipped cap, re-exported so the field's spent state and the server's
 *  own resolver can never key on two different numbers. */
export const VOUCHER_ATTEMPT_CAP = Voucher.VOUCHER_ATTEMPT_CAP;

export type VoucherPhase =
	| "idle"
	| "pending"
	| "applied"
	| "refused"
	| "dropped";

export type VoucherEntry = {
	readonly phase: VoucherPhase;
	/** The code the current phase is about — null while nothing has resolved. */
	readonly code: string | null;
	/** Cents off the eligible lines: the server's per-VAT-rate discount lines
	 *  summed, so the buyer is shown the amount and never the apportionment.
	 *  ZERO IS A REAL APPLIED ANSWER and the reason `applied` may never read as
	 *  "money off" — a code that only reveals hidden types, and one whose
	 *  discount is scoped to the very type it reveals, both arrive here at
	 *  zero. */
	readonly discountCents: number;
	/** The hidden ticket types this code offers this checkout. */
	readonly unlockedTypeIds: readonly string[];
	/** `checkout.voucherAttempts` — refused submissions on this checkout. */
	readonly attempts: number;
};

/** A checkout that has never seen a code. */
export const NO_VOUCHER: VoucherEntry = {
	phase: "idle",
	code: null,
	discountCents: 0,
	unlockedTypeIds: [],
	attempts: 0,
};

export const voucherDraft = state<string>("storefront/voucher-draft").memory(
	"",
);

export const voucherEntry = state<VoucherEntry>(
	"storefront/voucher-entry",
).memory(NO_VOUCHER);

/** The buyer pressed Einlösen — checkout.sync.ts is the sole subscriber. */
export const voucherSubmitted$ = new Subject<string>();

/** The buyer removed a code that stands. Same single subscriber. */
export const voucherCleared$ = new Subject<void>();

/**
 * The checkout's server answer folded onto what the field currently shows.
 * Pure, and the one place the phase machine lives, so its two callers —
 * the doc listener and the submit gesture — cannot answer the same document
 * differently.
 *
 * The submit call is what makes a resubmitted code terminate: it reduces the
 * would-be `pending` entry against the answer the document already holds, so a
 * code the server has already refused on this checkout is answered from the
 * document instead of waiting on a round trip the payload — which still names
 * that same code until the clearing write lands — would never provoke.
 *
 * Which code the field is asking about is the caller's to state, never this
 * function's to infer: a browser meeting a checkout for the first time asks
 * about whatever the document already answers, and a browser that has just
 * withdrawn a code asks about none.
 */
export function reduceVoucherEntry(
	entry: VoucherEntry,
	result: Checkout.VoucherResult | null | undefined,
	attempts: number,
): VoucherEntry {
	// The count is the server's on every path, including one this browser never
	// watched happen — it is what the field's spent state reads.
	const counted = { ...entry, attempts };
	// A cleared payload retracts the server's answer, never what the buyer was
	// told: the refusal clears the payload itself (checkout.sync.ts), so
	// resetting to idle here would erase the refusal's own explanation one
	// snapshot after showing it.
	if (result == null) return counted;
	// An answer naming a code other than the one the field is asking about is
	// the previous one — checkout.entity.ts's own contract for why `code` rides
	// both branches of the union — and this is the whole of what makes a
	// withdrawal stick. The server's answer outlives the buyer's removal by a
	// round trip (the payload clears first, `voucherResult` follows), so every
	// snapshot in that window still carries the applied result; without this
	// guard the field re-adopts the very code the buyer just removed, and the
	// following `result == null` then preserves it forever.
	if (result.code !== entry.code) return counted;
	if (result.status === "applied")
		return {
			phase: "applied",
			code: result.code,
			discountCents: result.discountCents,
			unlockedTypeIds: result.unlockedTypeIds,
			attempts,
		};
	// The same refusal re-delivered on a later snapshot: re-reading the document
	// is not a second refusal, and answering it again would turn a `dropped`
	// back into a bare `refused` and lose how the buyer got there.
	if (
		entry.code === result.code &&
		(entry.phase === "refused" || entry.phase === "dropped")
	)
		return counted;
	return {
		phase: entry.phase === "applied" ? "dropped" : "refused",
		code: result.code,
		discountCents: 0,
		unlockedTypeIds: [],
		attempts,
	};
}

/**
 * The code the checkout's `payload.voucher` must carry for the phase on
 * screen — and the whole of the refused-code trap's closure.
 *
 * Settle refuses `notEligible` for any code that does not resolve, so a
 * refused code left on the payload makes the checkout unpayable: the buyer
 * pays and settle voids. Deriving the payload from the phase rather than
 * writing it on a refusal branch makes that impossible to forget — only the
 * two phases where a code is genuinely outstanding name one, and refused,
 * dropped and idle all name null by construction.
 */
export const voucherCode = computed<string | null>(() =>
	voucherEntry.value.phase === "pending" ||
	voucherEntry.value.phase === "applied"
		? voucherEntry.value.code
		: null,
);

/** What a code that STANDS does to this basket — null unless one applied.
 *  Everything the page prices or reveals reads this rather than the phase
 *  cell, because pending, refused and dropped all mean the basket is priced
 *  as though no code existed. */
export const appliedVoucher = computed<Pick<
	VoucherEntry,
	"code" | "discountCents" | "unlockedTypeIds"
> | null>(() =>
	voucherEntry.value.phase === "applied"
		? {
				code: voucherEntry.value.code,
				discountCents: voucherEntry.value.discountCents,
				unlockedTypeIds: voucherEntry.value.unlockedTypeIds,
			}
		: null,
);
